(() => {
  const galleries = new Map();
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const initGallery = (gallery) => {
    if (galleries.has(gallery)) return;
    const slides = [...gallery.querySelectorAll('.kenze-auto-gallery__slide')];
    if (!slides.length) return;
    const controller = new AbortController();
    const options = { signal: controller.signal };
    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timer, frame, visible = false, generation = 0, start, suppressClick = false;
    const interval = Math.max(2500, Number(gallery.dataset.interval) || 4500);
    const warm = (i) => {
      const image = slides[i]?.querySelector('img');
      if (!image) return Promise.resolve();
      image.loading = 'eager';
      image.decoding = 'async';
      return image.decode ? image.decode().catch(() => {}) : Promise.resolve();
    };
    const schedule = () => {
      clearTimeout(timer);
      cancelAnimationFrame(frame);
      gallery.style.setProperty('--kenze-gallery-progress', '0');
      const paused = !visible || document.hidden || motion.matches || slides.length < 2;
      gallery.toggleAttribute('data-paused', paused);
      if (paused) return;
      const startedAt = performance.now();
      const updateProgress = (now) => {
        const progress = Math.min(1, Math.max(0, (now - startedAt) / interval));
        gallery.style.setProperty('--kenze-gallery-progress', String(progress));
        if (progress < 1) frame = requestAnimationFrame(updateProgress);
      };
      frame = requestAnimationFrame(updateProgress);
      timer = setTimeout(() => show(index + 1, false), interval);
    };
    const activate = () => {
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        slide.inert = i !== index;
        slide.setAttribute('aria-hidden', String(i !== index));
      });
      const source = slides[index].querySelector('.kenze-auto-gallery__link[href]');
      const wrapper = gallery.closest('.kenze-hero__panel, .kenze-showcase');
      if (source && wrapper) wrapper.querySelectorAll('.kenze-hero__card-link, .kenze-showcase__link').forEach((link) => {
        link.href = source.href;
        const label = source.getAttribute('aria-label');
        if (label) link.setAttribute('aria-label', label);
      });
      if (visible && !navigator.connection?.saveData) void warm((index + 1) % slides.length);
    };
    const show = async (next, user = true) => {
      clearTimeout(timer);
      cancelAnimationFrame(frame);
      if (!user) gallery.style.setProperty('--kenze-gallery-progress', '1');
      const token = ++generation;
      const target = (next + slides.length) % slides.length;
      const ready = warm(target);
      if (!user) await ready;
      if (token !== generation || !gallery.isConnected) return;
      if (!user && (!visible || document.hidden || motion.matches)) { schedule(); return; }
      index = target;
      activate();
      schedule();
    };
    if (slides.length > 1) {
      for (const [direction, label, symbol, step] of [['previous', gallery.dataset.previousLabel || 'Previous image', '‹', -1], ['next', gallery.dataset.nextLabel || 'Next image', '›', 1]]) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `kenze-gallery-control kenze-gallery-control--${direction}`;
        button.setAttribute('aria-label', label);
        button.textContent = symbol;
        button.addEventListener('click', () => show(index + step), options);
        gallery.appendChild(button);
      }
    }
    gallery.addEventListener('pointerdown', (event) => {
      suppressClick = false;
      if (event.target.closest('button, input, label, select, .kenze-gallery-restock') || event.pointerType === 'mouse') return;
      start = { x: event.clientX, y: event.clientY };
    }, { ...options, passive: true });
    gallery.addEventListener('pointerup', (event) => {
      if (!start) return;
      const x = event.clientX - start.x, y = event.clientY - start.y;
      start = null;
      if (Math.abs(x) > 40 && Math.abs(x) > Math.abs(y) * 1.5) {
        suppressClick = true;
        void show(index + (x < 0 ? 1 : -1));
      }
    }, { ...options, passive: true });
    gallery.addEventListener('pointercancel', () => { start = null; }, options);
    gallery.addEventListener('click', (event) => {
      if (suppressClick) { event.preventDefault(); event.stopPropagation(); suppressClick = false; }
    }, { ...options, capture: true });
    document.addEventListener('visibilitychange', schedule, options);
    motion.addEventListener('change', schedule, options);
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) { void warm(index); if (!navigator.connection?.saveData) void warm((index + 1) % slides.length); }
      schedule();
    }, { rootMargin: '100px' });
    observer.observe(gallery);
    activate();
    schedule();
    galleries.set(gallery, () => { ++generation; clearTimeout(timer); cancelAnimationFrame(frame); controller.abort(); observer.disconnect(); });
  };
  const initAll = () => {
    for (const [gallery, cleanup] of galleries) if (!gallery.isConnected) { cleanup(); galleries.delete(gallery); }
    document.querySelectorAll('[data-kenze-auto-gallery]').forEach(initGallery);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [gallery, cleanup] of galleries) if (event.target.contains(gallery)) { cleanup(); galleries.delete(gallery); }
  });
})();
